
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { take } from 'rxjs';
import { StateManagementService } from '../../shared/services/state-management.service';

@Component({
  selector: 'app-session-timeout',
  standalone: true,
  imports: [],
  templateUrl: './session-timeout.html',
})
export class SessionTimeout implements OnInit, OnDestroy {

  private stateService = inject(StateManagementService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private hostRef = inject(ElementRef<HTMLElement>);

  displayTime = '05:00';
  private remainingSeconds = 300; // 5 minutes
  private countdownEndsAt = 0;
  reasonText = `You've been inactive for a while. Your session will expire after ${this.remainingSeconds / 60} minutes of inactivity. Click <strong>Continue</strong> to stay signed in, or <strong>Logout</strong> to end your session.`;
  private timer?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    // ModalService sets `inert` on every <body> child except <modal-dialog> while a KB modal is open.
    // <app-root> (this popup's parent) would be inert, making the popup unclickable.
    // Attach the popup directly to <body> (like modal-dialog does) and make sure it is not inert.
    const host = this.hostRef.nativeElement as HTMLElement;
    document.body.appendChild(host);
    host.removeAttribute('inert');

    this.stateService
      .getSessionTimeoutReason()
      .pipe(take(1))
      .subscribe(reason => {
        if (reason === 'expired') {
          this.reasonText =
            'Your session has expired. Please sign in again to continue.';
        }
      });

    this.startCountdown();
  }

  ngOnDestroy(): void {
    clearInterval(this.timer);
    (this.hostRef.nativeElement as HTMLElement).remove();
  }

  private startCountdown(): void {
    clearInterval(this.timer);

    this.countdownEndsAt = Date.now() + this.remainingSeconds * 1000;
    this.updateDisplayFromDeadline();

    this.timer = setInterval(() => {
      this.updateDisplayFromDeadline();

      if (this.remainingSeconds > 0) {
        return;
      }

      this.logout();
    }, 1000);
  }

  private updateDisplayFromDeadline(): void {
    const remainingMs = this.countdownEndsAt - Date.now();
    this.remainingSeconds = Math.max(Math.ceil(remainingMs / 1000), 0);

    const minutes = Math.floor(this.remainingSeconds / 60);
    const seconds = this.remainingSeconds % 60;
    this.displayTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    // Ensure view refresh even if timer callbacks run outside Angular zone.
    this.cdr.detectChanges();
  }

  continueSession(): void {
    this.stateService
      .continueSession()
      .pipe(take(1))
      .subscribe(success => {
        if (!success) {
          this.auth.loginWithRedirect({
            authorizationParams: { prompt: 'login' },
            appState: {
              target: this.stateService.getLastActiveRoute(),
            },
          });
        }
      });
  }

  logout(): void {
    clearInterval(this.timer);
    this.stateService.clearAuthStateForLogout();
    new BroadcastChannel('auth').postMessage({ type: 'logout', reason: 'session-timeout' });
    this.auth.logout({
      logoutParams: {
        returnTo: `${window.location.origin}/login?reason=session-timeout`
      },
    });
  }
}